import { Entity } from '@backstage/catalog-model';
import {
  FEATURE_DEFINITIONS,
  FeatureDefinition,
} from '../../constants/featureDefinitions';

export interface OverflowTabRoute {
  title: string;
  path: string;
  /** Whether this tab can be opened for the current entity. */
  available: boolean;
  /** Shown when available is false. */
  unavailableReason?: string;
}

const TAB_TITLE_TO_FEATURE_ID: Record<string, string> = {
  Docs: 'techdocs',
  'CI/CD': 'github-actions-cicd',
  Kubernetes: 'kubernetes',
  'Pull Requests': 'pull-requests',
  'GitHub Issues': 'github-issues',
  'Code Insights': 'code-insights',
  Datadog: 'datadog',
  ADRs: 'adrs',
  'Tech Radar': 'tech-radar',
  'Score card': 'score-card',
  Feedback: 'feedback',
  'Q&A': 'qeta',
  ECS: 'aws-ecs',
};

const FEATURES_BY_TAB_PATH = new Map<string, FeatureDefinition[]>();
for (const feature of FEATURE_DEFINITIONS) {
  if (!feature.tabPath) continue;
  const existing = FEATURES_BY_TAB_PATH.get(feature.tabPath) ?? [];
  existing.push(feature);
  FEATURES_BY_TAB_PATH.set(feature.tabPath, existing);
}

function normalizeRoutePath(path: string): string {
  if (!path || path === '/') return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

function getFeaturesForTab(title: string, path: string): FeatureDefinition[] {
  const byTitle = TAB_TITLE_TO_FEATURE_ID[title];
  if (byTitle) {
    const feature = FEATURE_DEFINITIONS.find(f => f.id === byTitle);
    return feature ? [feature] : [];
  }
  return FEATURES_BY_TAB_PATH.get(normalizeRoutePath(path)) ?? [];
}

/** Evaluates the route `if` guard. No `if` means always available. */
export function isTabFeatureAvailable(
  entity: Entity,
  routeIf?: (entity: Entity) => boolean,
): boolean {
  if (!routeIf) return true;
  return routeIf(entity);
}

/** Human-readable reason when a tab cannot be opened for this entity. */
export function getTabUnavailableReason(
  entity: Entity,
  title: string,
  path: string,
  routeIf?: (entity: Entity) => boolean,
): string | undefined {
  if (isTabFeatureAvailable(entity, routeIf)) return undefined;

  // CI/CD can be enabled by either GitHub Actions or AWS CodeBuild.
  // Keep this special-case before generic annotation handling so we show
  // OR-guidance instead of only the GitHub annotation requirement.
  if (title === 'CI/CD') {
    return 'Add github.com/project-slug or aws.amazon.com/aws-codebuild-project-arn';
  }

  if (title === 'Docs') {
    return 'Add backstage.io/techdocs-ref or backstage.io/techdocs-entity';
  }

  const features = getFeaturesForTab(title, path);
  const withRequirements = features.filter(
    f =>
      (f.requiredAnnotations && f.requiredAnnotations.length > 0) ||
      (f.anyOfAnnotations && f.anyOfAnnotations.length > 0),
  );

  if (withRequirements.length > 0) {
    const annotations = new Set<string>();
    for (const f of withRequirements) {
      for (const a of f.requiredAnnotations ?? []) {
        annotations.add(a);
      }
      for (const a of f.anyOfAnnotations ?? []) {
        annotations.add(a);
      }
    }
    return `Add catalog annotation(s): ${[...annotations].join(', ')}`;
  }

  if (title === 'GraphiQL') {
    return 'Only for API entities with spec.type: graphql';
  }

  return 'Not configured for this entity';
}

/** Build overflow route metadata for the More tabs dialog. */
export function toOverflowTabRoute(
  entity: Entity,
  title: string,
  path: string,
  routeIf?: (entity: Entity) => boolean,
): OverflowTabRoute {
  const available = isTabFeatureAvailable(entity, routeIf);
  return {
    title,
    path,
    available,
    unavailableReason: available
      ? undefined
      : getTabUnavailableReason(entity, title, path, routeIf),
  };
}
